import { useState, useEffect } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import type { Dispatch } from "@reduxjs/toolkit";
import type { ProjectDetailed, TicketInProject } from "../generated-axios-client";
import { client } from "../api-client";
import { projectActions } from "../../features/project/projectSlice";
import { responseUnwrap } from "../../common/functions";

const sortByPriority = (tickets: TicketInProject[]): TicketInProject[] =>
  [...tickets].sort((a, b) => {
    if (a.priority == null) return 1;
    if (b.priority == null) return -1;
    return a.priority - b.priority;
  });

export const useDragSort = (
  tickets: TicketInProject[],
  projectId: string,
  dispatch: Dispatch,
) => {
  const [sortedTickets, setSortedTickets] = useState<TicketInProject[]>(() =>
    sortByPriority(tickets),
  );
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    setSortedTickets(sortByPriority(tickets));
  }, [tickets]);

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = sortedTickets.findIndex((t) => t.id === String(active.id));
    const newIndex = sortedTickets.findIndex((t) => t.id === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedTickets, oldIndex, newIndex);
    const preIndex = reordered[newIndex - 1]?.priority ?? null;
    const postIndex = reordered[newIndex + 1]?.priority ?? null;
    const ticketId = sortedTickets[oldIndex].id!;
    const originalOrder = sortedTickets;

    setSortedTickets(reordered);
    setSavingId(ticketId);

    const response = await client.apiV1ManagementTicketAssignIndexPartialUpdate(ticketId, {
      pre_index: preIndex,
      post_index: postIndex,
    });

    if (response.status === 200) {
      const projectResponse = await client.apiV1ManagementProjectsRetrieve(projectId);
      const unwrapped = responseUnwrap<ProjectDetailed>(projectResponse);
      if (!("error" in unwrapped)) {
        dispatch(projectActions.storeProject(unwrapped));
      }
    } else {
      setSortedTickets(originalOrder);
    }

    setSavingId(null);
  };

  return { sortedTickets, savingId, onDragEnd };
};
